// pages/societes/EditSocieteModal.jsx
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { X, AlertCircle } from 'lucide-react';

export default function EditSocieteModal({ isOpen, onClose, onSuccess, societe }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pays, setPays] = useState([]);
  const [societes, setSocietes] = useState([]);  // ✅ Pour société mère

  const [formData, setFormData] = useState({
    RaisonSociale: '', Sigle: '', Adresse: '', Ville: '',
    Telephone: '', Email: '', ID_Pays: 1, EstActif: true,
    ID_Societe_Mere: ''  // ✅ Nouveau
  });

  useEffect(() => {
    if (isOpen && societe) {
      setFormData({
        RaisonSociale: societe.RaisonSociale || '',
        Sigle: societe.Sigle || '',
        Adresse: societe.Adresse || '',
        Ville: societe.Ville || '',
        Telephone: societe.Telephone || '',
        Email: societe.Email || '',
        ID_Pays: societe.ID_Pays || 1,
        EstActif: societe.EstActif === true || societe.EstActif === 1,
        ID_Societe_Mere: societe.ID_Societe_Mere || ''
      });
      fetchPays();
      fetchSocietes();  // ✅ Récupérer la liste des sociétés mères
    }
  }, [isOpen, societe]);

  const fetchPays = async () => {
    try {
      const res = await api.get('/pays');
      setPays(res.data.pays || []);
    } catch (err) {
      console.error('Erreur chargement pays:', err);
    }
  };

  // ✅ Ajout de la fonction fetchSocietes
  const fetchSocietes = async () => {
    try {
      const res = await api.get('/societes');
      setSocietes(res.data.societes || []);
    } catch (err) {
      console.error('Erreur chargement sociétés:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    if (!formData.RaisonSociale.trim()) { setError('La raison sociale est obligatoire'); setLoading(false); return; }

    try {
      const response = await api.put(`/societes/${societe.ID_Societe}`, {
        ...formData,
        ID_Societe_Mere: formData.ID_Societe_Mere || null  // ✅ Envoi avec gestion null
      });
      if (response.data.success) { onSuccess(); onClose(); }
      else { setError(response.data.message); }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la modification');
    } finally { setLoading(false); }
  };

  if (!isOpen || !societe) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-blue-600">✏️ Modifier la société</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        {error && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-center gap-2"><AlertCircle size={16} /> {error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Raison sociale *</label>
              <input type="text" className="w-full p-2 border rounded-lg text-sm" value={formData.RaisonSociale} onChange={(e) => setFormData({...formData, RaisonSociale: e.target.value})} required /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Sigle</label>
              <input type="text" className="w-full p-2 border rounded-lg text-sm" value={formData.Sigle} onChange={(e) => setFormData({...formData, Sigle: e.target.value.toUpperCase()})} maxLength={10} /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
            <input type="text" className="w-full p-2 border rounded-lg text-sm" value={formData.Adresse} onChange={(e) => setFormData({...formData, Adresse: e.target.value})} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
              <input type="text" className="w-full p-2 border rounded-lg text-sm" value={formData.Ville} onChange={(e) => setFormData({...formData, Ville: e.target.value})} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Pays</label>
              <select className="w-full p-2 border rounded-lg text-sm" value={formData.ID_Pays} onChange={(e) => setFormData({...formData, ID_Pays: e.target.value})}>
                {pays.map(p => <option key={p.ID_Pays} value={p.ID_Pays}>{p.Nom} ({p.Code})</option>)}</select></div>
          </div>

          {/* ✅ Ajout du champ Société Mère */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">🏢 Société mère (holding)</label>
            <select 
              className="w-full p-2 border rounded-lg text-sm" 
              value={formData.ID_Societe_Mere || ''}
              onChange={(e) => setFormData({...formData, ID_Societe_Mere: e.target.value || null})}
            >
              <option value="">Aucune (société indépendante)</option>
              {societes
                .filter(s => s.ID_Societe !== societe.ID_Societe)  // ✅ Exclut la société en cours d'édition
                .map(s => (
                  <option key={s.ID_Societe} value={s.ID_Societe}>
                    {s.RaisonSociale} ({s.Sigle})
                  </option>
                ))
              }
            </select>
            <p className="text-xs text-gray-400 mt-1">Définit la holding ou la société mère de cette filiale.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
              <input type="text" className="w-full p-2 border rounded-lg text-sm" value={formData.Telephone} onChange={(e) => setFormData({...formData, Telephone: e.target.value})} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" className="w-full p-2 border rounded-lg text-sm" value={formData.Email} onChange={(e) => setFormData({...formData, Email: e.target.value})} /></div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={formData.EstActif} onChange={(e) => setFormData({...formData, EstActif: e.target.checked})} className="w-4 h-4 text-blue-500 rounded" />
            <span className="text-sm text-gray-700">Société active</span></label>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm">Annuler</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition disabled:opacity-50 text-sm">{loading ? 'Enregistrement...' : 'Modifier'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}